import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Search, X, Loader, Download, ChevronLeft, ChevronRight } from 'lucide-react';
import JSZip from 'jszip';
import { PbrMaps } from '../stores/worldStore';
import { putObject } from '../utils/uploadToStorage';
import './PBRBrowser.css';

// Production uses same-origin Vercel serverless functions (api/ambientcg/*);
// dev falls back to the local proxy server (server/server.js on 8349)
const SOCKET_URL = import.meta.env.PROD
  ? ''
  : (import.meta.env.VITE_SOCKET_URL || `http://localhost:${import.meta.env.VITE_SERVER_PORT || '8349'}`);

interface AmbientCGAsset {
  id: string;
  thumbnailUrl: string;
  downloadUrl: string;
  downloadSize: number;
}

interface PBRBrowserProps {
  onApply: (pbrMaps: PbrMaps) => void;
  onClose: () => void;
}

const RESULTS_PER_PAGE = 24;

// Map name detection (same logic as PrimitiveControls zip handler)
const MAP_KEYWORDS: Record<string, string[]> = {
  color: ['color', 'diffuse', 'albedo', 'basecolor', 'col', 'diff'],
  normal: ['normal', 'norm', 'nrm', 'normalgl', 'normaldx', 'normalogl'],
  roughness: ['roughness', 'rough', 'rgh'],
  ao: ['ao', 'ambientocclusion', 'occlusion', 'occ'],
  metallic: ['metallic', 'metalness', 'metal'],
};

const getMapType = (name: string): string => {
  const baseName = name.split('/').pop()?.split('.')[0] || '';
  const segments = baseName.split(/[_-]/);
  return segments[segments.length - 1].toLowerCase();
};

const findMap = (files: string[], keywords: string[]): string | undefined => {
  return files.find((name) => {
    const suffix = getMapType(name);
    return keywords.some((kw) => suffix === kw || suffix.includes(kw));
  });
};

const PBRBrowser: React.FC<PBRBrowserProps> = ({ onApply, onClose }) => {
  const [query, setQuery] = useState('');
  const [assets, setAssets] = useState<AmbientCGAsset[]>([]);
  const [totalResults, setTotalResults] = useState(0);
  const [offset, setOffset] = useState(0);
  const [loading, setLoading] = useState(false);
  const [applying, setApplying] = useState<string | null>(null);
  const [applyProgress, setApplyProgress] = useState('');
  const [error, setError] = useState<string | null>(null);
  const searchTimeout = useRef<ReturnType<typeof setTimeout> | null>(null);
  const panelRef = useRef<HTMLDivElement>(null);

  // Block pointer events from reaching OrbitControls
  useEffect(() => {
    const el = panelRef.current;
    if (!el) return;
    const stop = (e: Event) => e.stopPropagation();
    const events = ['pointerdown', 'pointermove', 'pointerup', 'mousedown', 'mousemove', 'mouseup', 'wheel', 'touchstart', 'touchmove', 'touchend'];
    events.forEach((evt) => el.addEventListener(evt, stop, { capture: true }));
    return () => { events.forEach((evt) => el.removeEventListener(evt, stop, { capture: true })); };
  }, []);

  const fetchAssets = useCallback(async (searchQuery: string, pageOffset: number) => {
    setLoading(true);
    setError(null);
    try {
      const params = new URLSearchParams({
        type: 'material',
        limit: String(RESULTS_PER_PAGE),
        offset: String(pageOffset),
        sort: searchQuery ? 'popular' : 'popular',
        include: 'downloads,thumbnails',
      });
      if (searchQuery.trim()) {
        params.set('q', searchQuery.trim());
      }

      const res = await fetch(`${SOCKET_URL}/api/ambientcg/assets?${params}`);
      if (!res.ok) throw new Error(`API error: ${res.status}`);
      const data = await res.json();

      const mapped: AmbientCGAsset[] = (data.assets || []).map((asset: any) => {
        const thumb = asset.thumbnails?.['256-JPG-242424'] || asset.thumbnails?.['256-PNG'] || '';
        const dl = asset.downloads?.find((d: any) => d.attributes === '1K-JPG') ||
                   asset.downloads?.find((d: any) => d.attributes === '2K-JPG') ||
                   asset.downloads?.[0];
        return {
          id: asset.id,
          thumbnailUrl: thumb,
          downloadUrl: dl?.url || '',
          downloadSize: dl?.size || 0,
        };
      });

      setAssets(mapped);
      setTotalResults(data.totalResults || 0);
    } catch (err) {
      console.error('[PBRBrowser] Fetch error:', err);
      setError(err instanceof Error ? err.message : 'Failed to load materials');
    } finally {
      setLoading(false);
    }
  }, []);

  // Initial load
  useEffect(() => {
    fetchAssets('', 0);
  }, [fetchAssets]);

  // Debounced search
  const handleSearch = (value: string) => {
    setQuery(value);
    if (searchTimeout.current) clearTimeout(searchTimeout.current);
    searchTimeout.current = setTimeout(() => {
      setOffset(0);
      fetchAssets(value, 0);
    }, 400);
  };

  const handlePage = (dir: 'next' | 'prev') => {
    const newOffset = dir === 'next' ? offset + RESULTS_PER_PAGE : Math.max(0, offset - RESULTS_PER_PAGE);
    setOffset(newOffset);
    fetchAssets(query, newOffset);
  };

  // Upload a blob to S3
  const uploadToS3 = async (blob: Blob, fileName: string, contentType: string): Promise<string> => {
    const key = `textures/acg-${Date.now()}-${fileName}`;
    return putObject(key, blob, contentType);
  };

  // Download zip, extract PBR maps, upload to S3, apply
  const handleApply = async (asset: AmbientCGAsset) => {
    if (!asset.downloadUrl || applying) return;

    setApplying(asset.id);
    setApplyProgress('Downloading...');
    try {
      // Download the zip via our server proxy to bypass CORS
      const res = await fetch(`${SOCKET_URL}/api/ambientcg/download?url=${encodeURIComponent(asset.downloadUrl)}`);
      if (!res.ok) throw new Error(`Download failed: ${res.status}`);
      const zipBlob = await res.blob();

      setApplyProgress('Extracting maps...');
      const zip = await JSZip.loadAsync(zipBlob);

      const imageExts = ['jpg', 'jpeg', 'png', 'webp', 'tga'];
      const files = Object.keys(zip.files).filter((name) => {
        const ext = name.split('.').pop()?.toLowerCase();
        return ext && imageExts.includes(ext) && !name.startsWith('__MACOSX');
      });

      const colorFile = findMap(files, MAP_KEYWORDS.color);
      if (!colorFile) throw new Error('No color map found in zip');

      const normalFile = findMap(files, MAP_KEYWORDS.normal);
      const roughnessFile = findMap(files, MAP_KEYWORDS.roughness);
      const aoFile = findMap(files, MAP_KEYWORDS.ao);
      const metallicFile = findMap(files, MAP_KEYWORDS.metallic);

      const uploadMap = async (fileName: string, label: string): Promise<string> => {
        setApplyProgress(`Uploading ${label}...`);
        const data = await zip.files[fileName].async('blob');
        const ext = fileName.split('.').pop()?.toLowerCase() || 'jpg';
        const ct = ext === 'png' ? 'image/png' : 'image/jpeg';
        return uploadToS3(data, `${asset.id}-${fileName.split('/').pop()}`, ct);
      };

      const result: PbrMaps = { colorUrl: '' };
      result.colorUrl = await uploadMap(colorFile, 'color');
      if (normalFile) result.normalUrl = await uploadMap(normalFile, 'normal');
      if (roughnessFile) result.roughnessUrl = await uploadMap(roughnessFile, 'roughness');
      if (aoFile) result.aoUrl = await uploadMap(aoFile, 'AO');
      if (metallicFile) result.metallicUrl = await uploadMap(metallicFile, 'metallic');

      setApplyProgress('Done!');
      onApply(result);
    } catch (err) {
      console.error('[PBRBrowser] Apply error:', err);
      setError(err instanceof Error ? err.message : 'Failed to apply material');
    } finally {
      setApplying(null);
      setApplyProgress('');
    }
  };

  const totalPages = Math.ceil(totalResults / RESULTS_PER_PAGE);
  const currentPage = Math.floor(offset / RESULTS_PER_PAGE) + 1;

  return (
    <div ref={panelRef} className="pbr-browser-overlay" onClick={onClose}>
      <div className="pbr-browser-modal" onClick={(e) => e.stopPropagation()}>
        <div className="pbr-browser-header">
          <h2>PBR Material Library</h2>
          <span className="pbr-browser-subtitle">Powered by ambientCG &middot; CC0 License</span>
          <button className="pbr-browser-close" onClick={onClose}><X size={18} /></button>
        </div>

        <div className="pbr-browser-search">
          <Search size={16} />
          <input
            type="text"
            placeholder="Search materials (wood, metal, brick, concrete...)"
            value={query}
            onChange={(e) => handleSearch(e.target.value)}
            autoFocus
          />
          {query && (
            <button className="pbr-search-clear" onClick={() => { setQuery(''); setOffset(0); fetchAssets('', 0); }}>
              <X size={14} />
            </button>
          )}
        </div>

        {error && <div className="pbr-browser-error">{error}</div>}

        <div className="pbr-browser-grid">
          {loading ? (
            <div className="pbr-browser-loading">
              <Loader size={24} className="s3-spinner" />
              <span>Loading materials...</span>
            </div>
          ) : assets.length === 0 ? (
            <div className="pbr-browser-empty">No materials found</div>
          ) : (
            assets.map((asset) => (
              <button
                key={asset.id}
                className={`pbr-browser-item ${applying === asset.id ? 'applying' : ''}`}
                onClick={() => handleApply(asset)}
                disabled={!!applying}
                title={`${asset.id} (${(asset.downloadSize / 1024 / 1024).toFixed(1)} MB)`}
              >
                {asset.thumbnailUrl ? (
                  <img src={asset.thumbnailUrl} alt={asset.id} loading="lazy" />
                ) : (
                  <div className="pbr-thumb-placeholder" />
                )}
                <span className="pbr-item-label">{asset.id}</span>
                {applying === asset.id && (
                  <div className="pbr-item-overlay">
                    <Loader size={16} className="s3-spinner" />
                    <span>{applyProgress}</span>
                  </div>
                )}
              </button>
            ))
          )}
        </div>

        <div className="pbr-browser-footer">
          <span className="pbr-browser-count">{totalResults.toLocaleString()} materials</span>
          <div className="pbr-browser-pagination">
            <button disabled={offset === 0 || !!applying} onClick={() => handlePage('prev')}>
              <ChevronLeft size={16} /> Prev
            </button>
            <span>{currentPage} / {totalPages}</span>
            <button disabled={offset + RESULTS_PER_PAGE >= totalResults || !!applying} onClick={() => handlePage('next')}>
              Next <ChevronRight size={16} />
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PBRBrowser;
